import { NAVIGATION_ROUTES } from '../../constant';
import { AADHAAR_DOC_TYPE } from '../../interfaces/Kyc';
import {
  KYC_POPUP_MESSAGE_SOURCE,
  KYC_POPUP_NONCE_KEY,
  getPendingKycSession,
  setPendingKycSession,
} from './helper';

/** Where SurePass is configured to return the browser. */
export const KYC_CALLBACK_PATHS = [
  NAVIGATION_ROUTES.KYC_CALLBACK,
  `${NAVIGATION_ROUTES.KYC_CALLBACK}.html`,
];

/**
 * Handles a DigiLocker return before React exists.
 *
 * Two things have to happen earlier than any component can manage.
 *
 * The `clientId` has to be parked before the router touches the URL. Every path
 * out of an unmatched return URL drops the query string — the catch-all
 * `<Navigate to="/wallet" replace>` and the `/kyc` guard's redirect to sign-in
 * both do — and with no SurePass webhook, a lost `clientId` means the user
 * authenticated at DigiLocker for nothing and the next attempt pays for another
 * billed `initialize`.
 *
 * And the SPA must not *boot* on the callback path at all. Booting runs silent
 * login in that window, and a failed reauth calls `logout()` →
 * `localStorage.clear()` — which, in the popup case, wipes the session for the
 * opener too, since localStorage is shared across the origin. A route component
 * cannot prevent that: by the time it mounts, `Routes.tsx` has already scheduled
 * the reauth. Only a pre-render hook can.
 *
 * `public/kyc-callback.html` normally does this job. This is the safety net for
 * when it never runs — a host that strips `.html` (and the query with it), a
 * cached redirect, or a backend change to the configured return URL.
 *
 * @returns `true` when the app must not render, because this window is on its
 * way somewhere else.
 */
export function bootstrapKycReturn(): boolean {
  try {
    const { pathname, search, origin } = window.location;
    const params = new URLSearchParams(search || '');

    // Vendor param naming is unconfirmed, so both spellings are accepted — same
    // as the app's own return handling.
    const clientId = params.get('client_id') || params.get('clientId');

    // Park before anything can navigate. Never overwrites a live session: the
    // parked copy carries `url`/`returnTo` that the query string does not.
    if (clientId && !getPendingKycSession()) {
      setPendingKycSession({ clientId, docType: AADHAAR_DOC_TYPE });
    }

    if (!KYC_CALLBACK_PATHS.includes(pathname)) return false;

    // Popup path: hand the result to the app, which never unloaded.
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(
        {
          source: KYC_POPUP_MESSAGE_SOURCE,
          nonce: localStorage.getItem(KYC_POPUP_NONCE_KEY),
          clientId,
          status: params.get('status'),
        },
        origin,
      );
      // Closed even when the message cannot be matched. The opener treats a
      // vanished window as unresolved, which keeps the pending session alive for
      // another attempt — a better outcome than booting the SPA here.
      window.close();
      return true;
    }

    // Top-level return: popup blocked, an installed iOS PWA (where window.open
    // leaves no opener), or DigiLocker handing back in a fresh tab. Forward to
    // /kyc with the query intact, which is where completion runs.
    window.location.replace(`${NAVIGATION_ROUTES.KYC}${search || ''}`);
    return true;
  } catch {
    // Never block the app from starting over this. The parked session and the
    // completion hook still cover the common case.
    return false;
  }
}
